// Centralized application constants

import stripeConfig from "@root/config/stripe.json";
export const HOME_PATH = "/app";
export const DASHBOARD_REDIRECT = HOME_PATH;

// NOTE: the patient portal is a separate SPA (apps/portal) on its own origin.
// All patient auth, invites, and binding live there — this clinician app has no
// portal routes, redirects, or invite-token handling.

// ─── Pricing tiers ──────────────────────────────────────────────────────────
// Everything here is derived from `config/stripe.json`, the same file the
// backend compiles in (`apps/app-api/src/usage/service.rs`), so advertised
// allowances and enforced limits cannot drift apart. Change the tier table in
// `scripts/setup_stripe_account.mjs` and re-run it — never edit the JSON.
//
// Note "tier" means the product tier (free / standard / high) and "interval"
// means the billing cadence (month / year). An earlier version of this file
// used `Tier` for the cadence; the two are distinct concepts now.

/** Product tiers, cheapest first. Index in this array is the upgrade order. */
export const TIER_ORDER = ["free", "standard", "high"] as const;
export type TierKey = (typeof TIER_ORDER)[number];

export type Interval = "month" | "year";

export type PlanLimits = {
  transcription_seconds: number;
  ai_generation: number;
};

/** One purchasable price: a tier at a billing cadence. */
export type TierPrice = {
  priceId: string;
  interval: Interval;
  /** Charge in cents. */
  amount: number;
  /** Formatted for display, e.g. "$30". */
  display: string;
};

export type TierPlan = {
  tier: TierKey;
  name: string;
  /** Per-seat, per-billing-period allowance. */
  limits: PlanLimits;
  /** Empty for the free tier, which is never charged. */
  prices: Partial<Record<Interval, TierPrice>>;
  /** The tier we steer people to. */
  highlight: boolean;
};

const _fmt = (cents: number) =>
  cents % 100 === 0
    ? `$${(cents / 100).toFixed(0)}`
    : `$${(cents / 100).toFixed(2)}`;

function buildPaidTier(tier: TierKey): TierPlan | null {
  const entries = stripeConfig.prices.filter((p) => p.tier === tier);
  if (entries.length === 0) return null;

  const prices: Partial<Record<Interval, TierPrice>> = {};
  for (const entry of entries) {
    prices[entry.interval as Interval] = {
      priceId: entry.id,
      interval: entry.interval as Interval,
      amount: entry.amount,
      display: _fmt(entry.amount),
    };
  }

  return {
    tier,
    // "Standard Monthly" -> "Standard". The tier name is the price name minus
    // its cadence, so it stays correct if the catalogue is renamed.
    name: entries[0].name.replace(/\s+(Monthly|Annual)$/i, ""),
    limits: entries[0].limits,
    prices,
    highlight: tier === "standard",
  };
}

const FREE_TIER: TierPlan = {
  tier: "free",
  name: stripeConfig.freePlan.name,
  limits: stripeConfig.freePlan.limits,
  prices: {},
  highlight: false,
};

/** Every tier, cheapest first — the display order for the pricing panels. */
export const TIERS: TierPlan[] = [
  FREE_TIER,
  ...TIER_ORDER.filter((t) => t !== "free")
    .map(buildPaidTier)
    .filter((t): t is TierPlan => t !== null),
];

/** The purchasable tiers. */
export const PAID_TIERS: TierPlan[] = TIERS.filter((t) => t.tier !== "free");

export function tierByKey(tier: TierKey): TierPlan | undefined {
  return TIERS.find((t) => t.tier === tier);
}

/**
 * The tier a subscription is on.
 *
 * `price_id` is the only signal available — the API exposes no tier field — so
 * an unrecognised or absent price resolves to free, mirroring the backend's
 * `plan_limits_for_price` fallback.
 */
export function tierForPriceId(priceId: string | null | undefined): TierKey {
  if (!priceId) return "free";
  const match = stripeConfig.prices.find((p) => p.id === priceId);
  return (match?.tier as TierKey) ?? "free";
}

/** The interval a subscription is billed on, or undefined if not purchasable. */
export function intervalForPriceId(
  priceId: string | null | undefined
): Interval | undefined {
  if (!priceId) return undefined;
  const match = stripeConfig.prices.find((p) => p.id === priceId);
  return match?.interval as Interval | undefined;
}

/**
 * Tiers strictly above `current`.
 *
 * The billing page offers upgrades only — downgrades and cancellation go
 * through the Stripe portal, which is the system of record for those changes.
 */
export function upgradeTiersFrom(current: TierKey): TierPlan[] {
  const rank = TIER_ORDER.indexOf(current);
  return TIERS.filter((t) => TIER_ORDER.indexOf(t.tier) > rank);
}

/**
 * How much the annual price saves against paying monthly, e.g. "2 months free".
 * Derived from the amounts so it cannot go stale when prices change.
 */
export function annualSavingsLabel(plan: TierPlan): string | undefined {
  const { month, year } = plan.prices;
  if (!month || !year) return undefined;
  const monthsPaid = year.amount / month.amount;
  const monthsFree = Math.round(12 - monthsPaid);
  if (monthsFree <= 0) return undefined;
  return `${monthsFree} month${monthsFree === 1 ? "" : "s"} free`;
}

/** Percentage saved by paying annually, e.g. 17. */
export function annualSavingsPercent(plan: TierPlan): number | undefined {
  const { month, year } = plan.prices;
  if (!month || !year) return undefined;
  const full = month.amount * 12;
  if (year.amount >= full) return undefined;
  return Math.round(((full - year.amount) / full) * 100);
}

/** price_id -> formatted amount. Used to label an existing subscription. */
export const prices: Record<string, string> = Object.fromEntries(
  stripeConfig.prices.map((p) => [p.id, _fmt(p.amount)])
);

/** Allowance on the free tier, when no plan has been purchased. */
export const FREE_LIMITS: PlanLimits = stripeConfig.freePlan.limits;

/** e.g. "50 hours" — for describing an allowance in marketing copy. */
export function describeHours(seconds: number): string {
  const hours = Math.round(seconds / 3600);
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

// Centralized template category values (must align with backend enum)
export const TEMPLATE_CATEGORIES = [
  "Note",
  "Report",
  "Letter",
  "Resource",
  "none",
] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];
export const TEMPLATE_CATEGORY_DEFAULT: TemplateCategory = "Note";
